import { useState } from 'react';
import { Mail, MessageSquare } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const SUPPORT_EMAIL = 'support@assistlyai.co.in';
const WHATSAPP = 'https://wa.me/919981361957?text=Hi%20AssistlyAI!';

export function Contact() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [openedMail, setOpenedMail] = useState(false);

  const sendToSupportEmail = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`AssistlyAI — message from ${name.trim()}`);
    const body = encodeURIComponent(
      [
        'AssistlyAI website contact form',
        '',
        `Name: ${name.trim()}`,
        `Reply email: ${email.trim()}`,
        '',
        message.trim(),
      ].join('\n'),
    );
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setOpenedMail(true);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto w-full">
          <Link
            to="/"
            className="text-sm font-medium text-[#25D366] hover:text-[#1DA851] transition-colors mb-8 inline-block"
          >
            ← Back to home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-2">
            Contact us
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Questions about AssistlyAI, billing, or your account? Email{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-[#25D366] hover:underline font-medium"
            >
              {SUPPORT_EMAIL}
            </a>
            , use the form below (opens your mail app to send to that address), or chat on WhatsApp.
          </p>

          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="flex items-center justify-center gap-2 w-full border-2 border-slate-200 dark:border-slate-600 hover:border-[#25D366] dark:hover:border-[#25D366] text-slate-900 dark:text-white px-6 py-4 rounded-full text-base font-semibold transition-colors mb-4"
          >
            <Mail className="w-5 h-5 text-[#25D366]" />
            Mail us directly
          </a>

          <a
            href={WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1DA851] text-white px-6 py-4 rounded-full text-base font-semibold transition-colors shadow-lg hover:shadow-green-500/30 mb-10"
          >
            <MessageSquare className="w-5 h-5" />
            Chat on WhatsApp
          </a>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40 p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
              Email support
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              Fill in all fields, then send — your email app will open with everything addressed to{' '}
              {SUPPORT_EMAIL}.
            </p>
            <form onSubmit={sendToSupportEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Name</Label>
                <Input
                  id="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  rows={5}
                  required
                />
              </div>
              <Button type="submit" className="w-full sm:w-auto gap-2">
                <Mail className="w-4 h-4" />
                Send to {SUPPORT_EMAIL}
              </Button>
            </form>
            {openedMail && (
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                If your email app didn&apos;t open, use &quot;Mail us directly&quot; above or copy{' '}
                {SUPPORT_EMAIL}.
              </p>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
